import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { LogOut, Mail, Phone, RefreshCw, UserRound, Download } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { Logo } from "@/components/Logo";
import { GuidesEditor, ServicesEditor, BlogEditor, RosieEditor, EmailsEditor } from "@/components/admin/ContentEditors";

const TABS = [["inbox", "Inbox"], ["services", "Services"], ["guides", "Guides"], ["blog", "Blog"], ["rosie", "Rosie"], ["emails", "Emails"]];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "rosevale_inbox_key";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const Login = ({ onLogin }) => {
  const [key, setKey] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.get(`${API}/chat/sessions`, { headers: { "x-admin-key": key } });
      sessionStorage.setItem(KEY_STORAGE, key);
      onLogin(key);
    } catch {
      setErr("That key isn't right.");
    }
  };
  return (
    <div className="min-h-screen bg-[#F5F2EB] flex items-center justify-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-white border border-[#103080]/10 p-8 lift-shadow" data-testid="inbox-login-form">
        <Logo />
        <h1 className="font-serif font-semibold text-2xl text-[#103080] mt-6">Admin area</h1>
        <p className="text-sm text-[#0F172A]/60 mt-1">Enter your admin key to read chats and edit website content.</p>
        <input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Admin key"
          data-testid="inbox-key-input"
          className="mt-5 w-full rounded-xl border border-[#103080]/15 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0090D0]"
        />
        {err && <p className="text-xs text-red-600 mt-2" data-testid="inbox-login-error">{err}</p>}
        <button type="submit" data-testid="inbox-login-submit" className="mt-4 w-full rounded-full bg-[#103080] text-white text-sm font-medium py-3 hover:bg-[#0090D0] transition-colors duration-300">Open inbox</button>
        <Link to="/" className="block text-center text-xs text-[#0F172A]/50 mt-4 hover:text-[#0090D0]">Back to website</Link>
      </form>
    </div>
  );
};

export default function InboxPage() {
  useSeo({ title: "Admin | Rosevale Accountants", description: "Private", path: "/inbox" });
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("inbox");
  const headers = { "x-admin-key": key };

  const load = async () => {
    const r = await axios.get(`${API}/chat/sessions`, { headers });
    setSessions(r.data.sessions);
  };
  useEffect(() => { if (key) load().catch(() => setKey("")); /* eslint-disable-next-line */ }, [key]);
  useEffect(() => {
    if (!active) return;
    axios.get(`${API}/chat/sessions/${active}`, { headers }).then((r) => setDetail(r.data)).catch(() => setDetail(null));
    // eslint-disable-next-line
  }, [active]);

  if (!key) return <Login onLogin={setKey} />;

  const shown = sessions.filter((s) => (filter === "handoff" ? s.handoff : true));

  return (
    <div className="min-h-screen bg-[#F5F2EB] text-[#0F172A]" data-testid="inbox-page">
      <header className="bg-white border-b border-[#103080]/10 px-5 sm:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-1" data-testid="admin-tabs">
            {TABS.map(([id, l]) => (
              <button key={id} type="button" onClick={() => setTab(id)} data-testid={`admin-tab-${id}`} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${tab === id ? "bg-[#103080] text-white" : "text-[#103080] hover:bg-[#F5F2EB]"}`}>{l}</button>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${API}/subscribers/export.csv?key=${encodeURIComponent(key)}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#103080]/15 px-3.5 py-2 text-xs font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0]" data-testid="inbox-export-subscribers">
            <Download size={14} /> Subscribers CSV
          </a>
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-[#103080]/15 px-3.5 py-2 text-xs font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0]" data-testid="inbox-refresh"><RefreshCw size={14} /> Refresh</button>
          <button type="button" onClick={() => { sessionStorage.removeItem(KEY_STORAGE); setKey(""); }} className="inline-flex items-center gap-1.5 rounded-full bg-[#103080] text-white px-3.5 py-2 text-xs font-medium hover:bg-[#0090D0]" data-testid="inbox-logout"><LogOut size={14} /> Log out</button>
        </div>
      </header>

      <div className="md:hidden px-5 pt-4 flex gap-2 overflow-x-auto">
        {TABS.map(([id, l]) => (
          <button key={id} type="button" onClick={() => setTab(id)} className={`shrink-0 rounded-full px-4 py-2 text-xs font-medium border ${tab === id ? "bg-[#103080] border-[#103080] text-white" : "bg-white border-[#103080]/15 text-[#103080]"}`}>{l}</button>
        ))}
      </div>

      {tab !== "inbox" && (
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
          {tab === "services" && <ServicesEditor headers={headers} />}
          {tab === "guides" && <GuidesEditor headers={headers} />}
          {tab === "blog" && <BlogEditor headers={headers} />}
          {tab === "rosie" && <RosieEditor headers={headers} />}
          {tab === "emails" && <EmailsEditor headers={headers} />}
        </div>
      )}

      {tab === "inbox" && (
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4">
          <div className="flex items-center gap-2 mb-4">
            {["all", "handoff"].map((f) => (
              <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`inbox-filter-${f}`} className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors duration-300 ${filter === f ? "bg-[#103080] border-[#103080] text-white" : "bg-white border-[#103080]/15 text-[#103080]"}`}>
                {f === "all" ? `All chats (${sessions.length})` : `Left details (${sessions.filter((s) => s.handoff).length})`}
              </button>
            ))}
          </div>
          <ul className="space-y-2" data-testid="inbox-session-list">
            {shown.length === 0 && <li className="text-sm text-[#0F172A]/50 p-4">No conversations yet.</li>}
            {shown.map((s) => (
              <li key={s.session_id}>
                <button type="button" onClick={() => setActive(s.session_id)} data-testid={`inbox-session-${s.session_id}`} className={`w-full text-left rounded-2xl border p-4 transition-colors duration-300 ${active === s.session_id ? "bg-white border-[#0090D0]" : "bg-white/70 border-[#103080]/10 hover:border-[#103080]/30"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-[#103080] truncate">{s.handoff ? s.handoff.name : "Anonymous visitor"}</p>
                    <span className="text-[11px] text-[#0F172A]/45 shrink-0">{fmt(s.last_active_at)}</span>
                  </div>
                  <p className="text-xs text-[#0F172A]/60 mt-1 line-clamp-2">{s.preview || "—"}</p>
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-[#0F172A]/45">
                    <span>{s.message_count || 0} messages</span>
                    {s.handoff && <span className="inline-flex items-center gap-1 text-[#0090D0] font-medium"><UserRound size={11} /> details left</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main className="lg:col-span-8">
          {!detail ? (
            <div className="h-full min-h-[320px] rounded-3xl bg-white/60 border border-dashed border-[#103080]/15 flex items-center justify-center text-sm text-[#0F172A]/50">Select a conversation to read it.</div>
          ) : (
            <div className="rounded-3xl bg-white border border-[#103080]/10 overflow-hidden" data-testid="inbox-detail">
              <div className="px-6 py-5 border-b border-[#103080]/10 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-serif font-semibold text-xl text-[#103080]">{detail.session.handoff ? detail.session.handoff.name : "Anonymous visitor"}</p>
                  <p className="text-xs text-[#0F172A]/50 mt-1">Started {fmt(detail.session.created_at)} · last page {detail.session.last_page || "/"}</p>
                  {detail.session.handoff?.message && (
                    <p className="mt-3 text-sm text-[#0F172A]/80 bg-[#F5F2EB] rounded-xl px-4 py-3 max-w-xl" data-testid="inbox-handoff-message"><span className="text-[10px] uppercase tracking-[0.15em] text-[#0F172A]/45 block mb-1">Their message</span>{detail.session.handoff.message}</p>
                  )}
                </div>
                {detail.session.handoff && (
                  <div className="flex flex-wrap gap-2">
                    <a href={`mailto:${detail.session.handoff.email}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#103080] text-white px-3.5 py-2 text-xs font-medium hover:bg-[#0090D0]"><Mail size={13} /> {detail.session.handoff.email}</a>
                    {detail.session.handoff.phone && <a href={`tel:${detail.session.handoff.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#103080]/15 px-3.5 py-2 text-xs font-medium text-[#103080]"><Phone size={13} /> {detail.session.handoff.phone}</a>}
                  </div>
                )}
              </div>
              <div className="px-6 py-6 space-y-3 max-h-[70vh] overflow-y-auto">
                {detail.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#103080] text-white" : "bg-[#F5F2EB] text-[#0F172A]/85"}`}>
                      <p className="text-[10px] uppercase tracking-[0.15em] opacity-60 mb-1">{m.role === "user" ? "Visitor" : "Assistant"} · {fmt(m.ts)}</p>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
      )}
    </div>
  );
}
